import { asve } from '../components/reports/shared/orve';
import { getCompetenciesSummary, getCompetenciesSummaryText } from '../components/reports/shared/assessment-verbiage';

/**
 * Generates JSON data for leadership reports that can be submitted to the backend
 * @param {object} candidate - The candidate data object
 * @param {boolean} isDevelop - True for develop report, false for staffing report
 * @returns {object} JSON data representing the leadership report
 */
export const generateLeadershipReportJson = (candidate, isDevelop) => {
  const baseReportData = {
    reportType: getReportTypeString(isDevelop),
    targetId: candidate.userId,
    targetName: candidate.name,
  };

  // Common sections for all report types
  const commonSections = {
    personalityResults: generatePersonalityResultsJson(candidate),
    acumenResults: generateAcumenResultsJson(candidate),
    valuesFitResults: generateValuesFitResultsJson(candidate),
    experienceResults: generateExperienceResultsJson(candidate),
    judgmentResults: generateJudgmentResultsJson(candidate),
  };

  if (isDevelop) {
    return {
      ...baseReportData,
      ...commonSections,
      competenciesResults: generateCompetenciesResultsJson(candidate),
    };
  }

  return {
    ...baseReportData,
    ...commonSections,
  };
};

/**
 * Convert report type number to string representation
 * @param {boolean} isDevelop
 * @returns {string}
 */
const getReportTypeString = (isDevelop) => {
  if (isDevelop) {
    return 'development';
  } else {
    return 'staffing';
  }
};

/**
 * Generate JSON for compenties results section
 * @param {object} candidate
 * @returns {object}
 */
const generateCompetenciesResultsJson = (candidate) => {
  return {
    overall: {
      overallLeadership: candidate.selfCompetencies?.scores?.overallLeadership || 0,
      text: getCompetenciesSummaryText(candidate),
    },
    ...(candidate.selfCompetencies?.variableKeys?.reduce((acc, value) => {
      acc[value] = {
        self: candidate.selfCompetencies?.scores?.[value],
        raters: candidate.raterCompetencies?.scores?.[value],
        summary: getCompetenciesSummary(candidate, value),
      };
      return acc;
    }, {}) || {}),
    exceedsExpectations: {
      self: candidate.selfCompetencies?.scores?.exceedsExpectations,
      raters: candidate.raterCompetencies?.scores?.exceedsExpectations,
    },
    exceptionalLeader: {
      self: candidate.selfCompetencies?.scores?.exceptionalLeader,
      raters: candidate.raterCompetencies?.scores?.exceptionalLeader,
    },
    openEndedComments: {
      greatestStrengths: candidate.selfCompetencies?.scores?.greatestStrengthCollection,
      opportunityForImprovement: candidate.selfCompetencies?.scores?.opportunityForImprovementCollection,
    },
  };
};

/**
 * Generate JSON for personality results section
 * @param {object} candidate
 * @returns {object}
 */
const generatePersonalityResultsJson = (candidate) => {
  const assessmentText = asve(candidate) || {};

  return {
    overall: {
      percentile: candidate.percentiles?.personality || 0,
      text: assessmentText?.personality || '',
    },
    honestyHumility: {
      score: candidate.personality?.scores?.honestyHumility?.overall || 0,
      text: assessmentText?.honestyHumility || '',
      sincerity: {
        score: candidate.personality?.scores?.honestyHumility?.sincerity || 0,
        text: assessmentText?.sincerity || '',
      },
      fairness: {
        score: candidate.personality?.scores?.honestyHumility?.fairness || 0,
        text: assessmentText?.fairness || '',
      },
    },
    extraversion: {
      score: candidate.personality?.scores?.extraversion?.overall || 0,
      text: assessmentText?.extraversion || '',
      socialSelfEsteem: {
        score: candidate.personality?.scores?.extraversion?.socialSelfEsteem || 0,
        text: assessmentText?.socialSelfEsteem || '',
      },
      socialBoldness: {
        score: candidate.personality?.scores?.extraversion?.socialBoldness || 0,
        text: assessmentText?.socialBoldness || '',
      },
      socialbility: {
        score: candidate.personality?.scores?.extraversion?.socialbility || 0,
        text: assessmentText?.socialbility || '',
      },
      liveliness: {
        score: candidate.personality?.scores?.extraversion?.liveliness || 0,
        text: assessmentText?.liveliness || '',
      },
    },
    conscientiousness: {
      score: candidate.personality?.scores?.conscientiousness?.overall || 0,
      text: assessmentText?.conscientiousness || '',
    },
    emotionalControl: {
      score: candidate.personality?.scores?.emotionalControl?.overall || 0,
      text: assessmentText?.emotionalControl || '',
      courageous: {
        score: candidate.personality?.scores?.emotionalControl?.courageous || 0,
        text: assessmentText?.courageous || '',
      },
      collectedness: {
        score: candidate.personality?.scores?.emotionalControl?.collectedness || 0,
        text: assessmentText?.collectedness || '',
      },
      independence: {
        score: candidate.personality?.scores?.emotionalControl?.independence || 0,
        text: assessmentText?.independence || '',
      },
    },
  };
};

/**
 * Generate JSON for aucuem results section
 * @param {object} candidate
 * @returns {object}
 */
const generateAcumenResultsJson = (candidate) => {
  const assessmentText = asve(candidate) || {};

  return {
    overall: {
      percentile: candidate.percentiles?.acumen || 0,
      text: assessmentText?.acumen || '',
    },
  };
};

/**
 * Generate JSON for values fit results section
 * @param {object} candidate
 * @returns {object}
 */
const generateValuesFitResultsJson = (candidate) => {
  const assessmentText = asve(candidate) || {};

  return {
    overall: {
      percentile: candidate.percentiles?.values || 0,
      text: assessmentText.values || '',
    },
    ...(candidate.values?.valuesScores?.reduce((acc, value) => {
      acc[value.key] = {
        name: candidate.values?.titles?.[value.key] || value.key,
        rating: value.rating,
        score: value.score,
        priority: value.priority,
        text: assessmentText[value.key] || '',
      };
      return acc;
    }, {}) || {}),
  };
};

/**
 * Generate JSON for experience results section
 * @param {object} candidate
 * @returns {object}
 */
const generateExperienceResultsJson = (candidate) => {
  const assessmentText = asve(candidate) || {};

  return {
    overall: {
      percentile: candidate.percentiles.experience || 0,
      text: assessmentText?.experience || '',
    },
    riskTaking: {
      score: candidate.experience?.scores?.riskTaking || 0,
      text: assessmentText?.riskTaking || '',
    },
    conflictResolution: {
      score: candidate.experience?.scores?.conflictResolution || 0,
      text: assessmentText?.conflictResolution || '',
    },
    dealingWithConflict: {
      score: candidate.experience?.scores?.dealingWithConflict || 0,
      text: assessmentText?.dealingWithConflict || '',
    },
    meetingFacilitation: {
      score: candidate.experience?.scores?.meetingFacilitation || 0,
      text: assessmentText?.meetingFacilitation || '',
    },
    directCommunication: {
      score: candidate.experience?.scores?.directCommunication || 0,
      text: assessmentText?.directCommunication || '',
    },
  };
};

/**
 * Generate JSON for \judgment results section
 * @param {object} candidate
 * @returns {object}
 */
const generateJudgmentResultsJson = (candidate) => {
  const assessmentText = asve(candidate) || {};

  return {
    judgment: {
      overall: {
        percentile: candidate.percentiles.judgment || 0,
        text: assessmentText?.judgment || '',
      },
      buildingCohesiveTeams: {
        score: candidate.situationalJudgment?.scores?.buildingCohesiveTeams || 0,
        text: assessmentText?.buildingCohesiveTeams || '',
      },
      buildingResilientTeams: {
        score: candidate.situationalJudgment?.scores?.buildingResilientTeams || 0,
        text: assessmentText?.buildingResilientTeams || '',
      },
      executingForResults: {
        score: candidate.situationalJudgment?.scores?.executingForResults || 0,
        text: assessmentText?.executingForResults || '',
      },
    },
  };
};

/**
 * Main function to generate leadership report JSON and submit to backend
 * @param {object} candidate - The candidate data object
 * @param {boolean} isDevelop - Staffing or develop report
 * @param {function} submitCallback - Optional callback to submit the report JSON to backend
 * @returns {object} The generated report JSON
 */
export const generateAndSubmitLeadershipReport = async (candidate, isDevelop, submitCallback) => {
  const reportJson = generateLeadershipReportJson(candidate, isDevelop);

  // If submitCallback is provided, use it to send the report to the backend
  if (submitCallback && typeof submitCallback === 'function') {
    return submitCallback(reportJson);
  }

  return reportJson;
};
